import { CheckIcon, ShirtIcon } from "@xola/icons";
import { isArray } from "lodash";
import React from "react";
import { Audit, FulfillmentUnitStatus, MerchandiseFulfillmentStatus, Purchase, PurchaseItem } from "types";
import { ItemProductImage } from "../../PurchaseProductImage";
import { TimelineItem, TimelinePill } from "../TimelineItem";
import { getMerchandiseWareLineItems } from "@/models/MerchandisePurchaseItem";
import { isMerchandiseItem } from "@/models/PurchaseItem";

interface FulfillmentUnitDelta {
    id?: ID;
    status?: { old?: string; new?: string };
    old?: null | { status?: string };
    new?: null | { id?: ID; status?: string };
}

interface AuditItemDelta {
    id: ID;
    fulfillmentStatus?: { old?: string; new?: string };
    fulfillmentUnits?: Record<string, FulfillmentUnitDelta>;
    lineItems?: Record<string, { id?: ID; quantity?: { old?: number; new?: number } }>;
}

interface OrderFulfillmentTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const OrderFulfilledTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: OrderFulfillmentTimelineItemProps) => {
    return (
        <OrderStatusTimelineItem
            purchase={purchase}
            item={item}
            audit={audit}
            showDate={showDate}
            status={MerchandiseFulfillmentStatus.FULFILLED}
            title="Order Fulfilled"
        />
    );
};

OrderFulfilledTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return supportsAudit(item, audit, MerchandiseFulfillmentStatus.FULFILLED);
};

export const OrderPartiallyFulfilledTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: OrderFulfillmentTimelineItemProps) => {
    return (
        <OrderStatusTimelineItem
            purchase={purchase}
            item={item}
            audit={audit}
            showDate={showDate}
            status={MerchandiseFulfillmentStatus.PARTIALLY_FULFILLED}
            title="Order Partially Fulfilled"
        />
    );
};

OrderPartiallyFulfilledTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return supportsAudit(item, audit, MerchandiseFulfillmentStatus.PARTIALLY_FULFILLED);
};

interface OrderStatusTimelineItemProps extends OrderFulfillmentTimelineItemProps {
    status: MerchandiseFulfillmentStatus;
    title: string;
}

const OrderStatusTimelineItem = ({ purchase, item, audit, showDate, status, title }: OrderStatusTimelineItemProps) => {
    if (!supportsAudit(item, audit, status) || !isMerchandiseItem(item)) {
        return null;
    }

    const lines = getFulfilledDeltaLines(item, audit);
    const linesToRender = lines.length > 0 ? lines : getAllVariantLines(item, audit);

    return (
        <TimelineItem
            title={title}
            audit={audit}
            icon={<CheckIcon />}
            color="success"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-y-2">
                <div className="flex w-full max-w-lg items-center space-x-3 rounded bg-gray-lighter p-3">
                    <ItemProductImage item={item} size="tiny" className="!mr-0" classNames={{ image: "h-8 w-8" }} />
                    <div className="text-sm font-bold">
                        <var data-var="item-name">{item.name}</var>
                    </div>
                </div>

                <div className="text-sm text-gray-dark">
                    <var data-var="customer-name">{purchase.customerName}</var>
                </div>

                <div className="space-y-1">
                    {linesToRender.map((line) => (
                        <div key={line}>
                            <TimelinePill className="!bg-green-lightish text-white" icon={<ShirtIcon />}>
                                <span className="flex max-w-200 items-start">
                                    <var data-var="variant-line" className="whitespace-normal break-words">
                                        {line}
                                    </var>
                                </span>
                            </TimelinePill>
                        </div>
                    ))}
                </div>
            </div>
        </TimelineItem>
    );
};

const supportsAudit = (item: PurchaseItem, audit: Audit, status: MerchandiseFulfillmentStatus) => {
    if (!isMerchandiseItem(item)) {
        return false;
    }

    const auditItem = getMatchingAuditItem(item, audit);
    if (!auditItem) {
        return false;
    }

    const hasUnitsFulfilled = Object.values(auditItem.fulfillmentUnits ?? {}).some((unit) => {
        // Delta format: { status: { old: "reserved", new: "fulfilled" } }
        const deltaFulfilled = unit.status?.new === FulfillmentUnitStatus.FULFILLED;
        // Replace format: { old: null, new: { status: "fulfilled", ... } }
        const replaceFulfilled = unit.new?.status === FulfillmentUnitStatus.FULFILLED;
        return deltaFulfilled || replaceFulfilled;
    });

    if (status === MerchandiseFulfillmentStatus.FULFILLED) {
        return auditItem.fulfillmentStatus?.new === MerchandiseFulfillmentStatus.FULFILLED;
    }

    if (status === MerchandiseFulfillmentStatus.PARTIALLY_FULFILLED) {
        // Explicit transition to partially_fulfilled (with real units being fulfilled, not a regression)
        if (auditItem.fulfillmentStatus?.new === MerchandiseFulfillmentStatus.PARTIALLY_FULFILLED) {
            return hasUnitsFulfilled;
        }

        // No fulfillmentStatus change = status stayed partially_fulfilled between events.
        // Show this tile whenever units were actually fulfilled in this audit.
        if (!auditItem.fulfillmentStatus) {
            return hasUnitsFulfilled;
        }
    }

    return false;
};

const getMatchingAuditItem = (item: PurchaseItem, audit: Audit): AuditItemDelta | undefined => {
    const auditItems: AuditItemDelta[] = isArray(audit.data?.items)
        ? audit.data?.items
        : Object.values(audit.data?.items ?? {});

    return auditItems.find((currentItem) => currentItem.id === item.id);
};

const getAllVariantLines = (item: PurchaseItem, audit?: Audit) => {
    if (!isMerchandiseItem(item)) {
        return [];
    }

    if (audit) {
        const auditItem = getMatchingAuditItem(item, audit);
        const auditLineItems = Object.values(auditItem?.lineItems ?? {});

        if (auditLineItems.length > 0) {
            const linesFromAudit = auditLineItems
                .map((auditLineItem) => {
                    const quantity = auditLineItem.quantity?.new;
                    const matchedLineItem = item.lineItems.find(
                        (storedLineItem) => storedLineItem.id === auditLineItem.id,
                    );

                    if (!quantity || !matchedLineItem?.name) {
                        return null;
                    }

                    return `${quantity}x ${matchedLineItem.name}`;
                })
                .filter(Boolean) as string[];

            if (linesFromAudit.length > 0) {
                return linesFromAudit;
            }
        }
    }

    return getMerchandiseWareLineItems(item).map((lineItem) => `${lineItem.quantity}x ${lineItem.name}`);
};

const getFulfilledDeltaLines = (item: PurchaseItem, audit: Audit) => {
    if (!isMerchandiseItem(item)) {
        return [];
    }

    const auditItem = getMatchingAuditItem(item, audit);
    const fulfillmentUnits = auditItem?.fulfillmentUnits;

    // No unit deltas in this audit — caller will fall back to getAllVariantLines
    if (!fulfillmentUnits) {
        return [];
    }

    const lineMap = new Map<string, number>();

    for (const unit of Object.values(fulfillmentUnits) as FulfillmentUnitDelta[]) {
        const unitId = unit.id ?? unit.new?.id;
        const hasFulfilledStatus =
            unit.status?.new === FulfillmentUnitStatus.FULFILLED ||
            unit.new?.status === FulfillmentUnitStatus.FULFILLED;

        if (!hasFulfilledStatus || !unitId) {
            continue;
        }

        const currentUnit = item.fulfillmentUnits?.find((storedUnit) => storedUnit.id === unitId);
        const templateId = currentUnit?.template?.id;

        if (!templateId) {
            continue;
        }

        const lineItem = item.lineItems.find((storedLineItem) => storedLineItem.template?.id === templateId);
        if (!lineItem) {
            continue;
        }

        const currentCount = lineMap.get(lineItem.name) ?? 0;
        lineMap.set(lineItem.name, currentCount + 1);
    }

    return [...lineMap.entries()].map(([name, quantity]) => `${quantity}x ${name}`);
};
